import { createTheme, alpha } from '@mui/material/styles';

function getTheme(config) {
    const isMobile = config.isMobile;
    
    // ========================================================================
    // 1. CẤU HÌNH PALETTE CƠ BẢN
    // ========================================================================
    const palette = {
        primary: {
            main: config.queryParams.primaryColor || config.primaryColor || '#007aff', // Khuyên dùng màu xanh iOS mặc định
        },
        secondary: {
            main: config.queryParams.secondaryColor || config.secondaryColor || '#34c759', // Xanh lá iOS
        },
        warning: {
            main: config.queryParams.warningColor || config.warningColor || '#ff9500', // Cam iOS
            contrastText: config.queryParams.warningColorText || config.warningColorText || '#ffffff',
            dark: config.queryParams.warningColorDark || config.warningColorDark || '#cc7700',
        },
        error: {
            main: config.queryParams.errorColor || config.errorColor || '#ff3b30', // Đỏ iOS
            contrastText: config.queryParams.errorColorText || config.errorColorText || '#ffffff',
            dark: config.queryParams.errorColorDark || config.errorColorDark || '#cc2e26',
        }
    };

    // Nếu là Mobile, chèn thêm background chuẩn iOS
    if (isMobile) {
        palette.background = {
            default: '#f2f2f7', // Nền xám nhạt (Grouped Background)
            paper: '#ffffff'    // Nền thẻ trắng (Card Background)
        };
        palette.divider = 'rgba(60, 60, 67, 0.12)'; // Màu viền chuẩn iOS
    }

    // ========================================================================
    // 2. CẤU HÌNH TYPOGRAPHY (FONT CHỮ)
    // ========================================================================
    const typography = {
        fontFamily: isMobile 
            ? "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'SF Pro Text', 'Helvetica Neue', Arial, sans-serif"
            : "'Roboto', 'Helvetica', 'Arial', sans-serif",
    };
    
    const components = {};

    // ========================================================================
    // 3. LOGIC STYLE CHUẨN IOS KHI IS_MOBILE = TRUE
    // ========================================================================
    if (isMobile) {
        
        // HÀM TẠO GLASSMORPHISM THÔNG MINH
        const createGlassmorphismStyle = (theme, ownerState) => {
            const colorName = ownerState.color && theme.palette[ownerState.color] ? ownerState.color : 'primary';
            const colorPalette = theme.palette[colorName];
            const mainColor = colorPalette.main;
            const contrastColor = colorPalette.contrastText || theme.palette.getContrastText(mainColor);

            return {
                background: alpha(mainColor, 1),
                backdropFilter: 'blur(10px)',
                WebkitBackdropFilter: 'blur(10px)',
                border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
                boxShadow: `0 4px 14px ${alpha(mainColor, 0.3)}`, // Bóng đổ tự động theo màu gốc
                color: contrastColor,
                '&:hover': {
                    background: alpha(mainColor, 1),
                    boxShadow: `0 4px 20px ${alpha(mainColor, 0.4)}`,
                }
            };
        };

        // --- ICON BUTTON ---
        components.MuiIconButton = {
            styleOverrides: {
                root: ({ theme, ownerState }) => {
                  if (ownerState.glass) {
                      return {
                          ...createGlassmorphismStyle(theme, ownerState),
                          borderRadius: '50%',
                      };
                  }
                  return {}; 
              }
            }
        };

        // --- BUTTON ---
        components.MuiButton = {
            // Tắt bóng đổ mặc định gắt gỏng của Material (Đưa ra defaultProps mới đúng chuẩn)
            defaultProps: {
                disableElevation: true,
            },
            styleOverrides: {
                root: ({ theme, ownerState }) => {
                    const colorName = ownerState.color && theme.palette[ownerState.color] ? ownerState.color : 'primary';
                    const mainColor = theme.palette[colorName]?.main || theme.palette.primary.main;

                    // 1. XỬ LÝ KÍCH THƯỚC ĐỘNG (Dynamic Sizing)
                    let padding = '8px 16px';     // Medium (Mặc định)
                    let fontSize = '1rem';
                    let borderRadius = '12px';

                    if (ownerState.size === 'small') {
                        padding = '4px 10px';     // Nút nhỏ gọn gàng
                        fontSize = '0.85rem';
                        borderRadius = '8px';     // Bo góc nhẹ hơn cho nút nhỏ đỡ bị lố
                    } else if (ownerState.size === 'large') {
                        padding = '12px 20px';    // Nút lớn (Call to action)
                        fontSize = '1.1rem';
                        borderRadius = '14px';    // Bo góc lớn chuẩn iOS
                    }

                    const baseSx = {
                        borderRadius: borderRadius,
                        fontWeight: '600', 
                        fontSize: fontSize, 
                        textTransform: 'none', 
                        padding: padding,
                        transition: 'all 0.2s ease-in-out',
                    };

                    // 2. Chế độ Glass (Trong suốt)
                    if (ownerState.glass) {
                        if (ownerState.variant === 'text') {
                            return { ...baseSx, color: mainColor, '&:hover': { background: alpha(mainColor, 0.08) } };
                        }
                        return { ...baseSx, ...createGlassmorphismStyle(theme, ownerState), borderRadius: 9999 };
                    }
                    
                    // 3. Chế độ Contained (Mặc định) -> Đổ bóng nhẹ theo TỪNG màu
                    if (ownerState.variant === "contained") {
                        // Nếu là nút disable, tắt bóng đổ
                        if (ownerState.disabled) {
                            return baseSx;
                        }

                        return {
                            ...baseSx,
                            boxShadow: `0 4px 14px ${alpha(mainColor, 0.25)}`,
                            '&:hover': { 
                                backgroundColor: theme.palette[colorName]?.dark,
                                boxShadow: `0 4px 14px ${alpha(mainColor, 0.35)}`,
                            },
                        };
                    }

                    return baseSx;
                },
            },
        };

        // --- TEXT FIELD / INPUT iOS ---
        components.MuiOutlinedInput = {
            styleOverrides: {
                root: {
                    borderRadius: '12px',
                    backgroundColor: '#ffffff',
                    transition: 'all 0.2s ease',
                    '&.Mui-focused': {
                        backgroundColor: '#ffffff',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.04)', // Nổi lên nhẹ khi focus
                    }
                }
            }
        };

        // --- SWITCH TOGGLE iOS ---
        components.MuiSwitch = {
            styleOverrides: {
                root: {
                    width: 42,
                    height: 26,
                    padding: 0,
                    margin: 8,
                },
                switchBase: {
                    padding: 1,
                    '&.Mui-checked': {
                        transform: 'translateX(16px)',
                        color: '#fff',
                        '& + .MuiSwitch-track': {
                            backgroundColor: '#34c759', // Màu xanh chuối kinh điển của Apple
                            opacity: 1,
                            border: 0,
                        },
                    },
                },
                thumb: { width: 24, height: 24, boxShadow: '0 2px 4px 0 rgb(0 35 11 / 20%)' },
                track: {
                    borderRadius: 26 / 2,
                    backgroundColor: '#e9e9ea',
                    opacity: 1,
                    transition: 'background-color 500ms',
                },
            },
        };

        // --- ACCORDION (IOS SETTINGS STYLE) ---
        components.MuiAccordion = {
            styleOverrides: {
                root: {
                    backgroundColor: '#ffffff', 
                    marginBottom: '12px !important', 
                    borderRadius: '16px !important', 
                    boxShadow: '0 2px 8px rgba(0,0,0,0.04)', 
                    '&:before': { display: 'none' },
                    '&.Mui-expanded': { margin: '0 0 12px 0 !important' }
                }
            }
        };
        components.MuiAccordionSummary = {
            styleOverrides: {
                root: {
                    padding: '0 16px', minHeight: '56px !important', 
                    '&.Mui-expanded': { minHeight: '56px !important', borderBottom: '1px solid #f2f2f7' }
                },
                content: { margin: '12px 0 !important', '&.Mui-expanded': { margin: '12px 0 !important' } }
            }
        };
        components.MuiAccordionDetails = {
            styleOverrides: {
                root: { padding: '16px', backgroundColor: '#ffffff', borderBottomLeftRadius: '16px', borderBottomRightRadius: '16px' }
            }
        };
    } 
    // ========================================================================
    // 4. BACKUP LOGIC CHO DESKTOP
    // ========================================================================
    else {
        components.MuiButton = {
            styleOverrides: {
                root: ({ ownerState, theme }) => {
                    if (ownerState.color === "warning" && ownerState.variant === "contained") {
                        return {
                            color: theme.palette.warning.contrastText,
                            backgroundColor: theme.palette.warning.main,
                            '&:hover': { backgroundColor: theme.palette.warning.dark },
                        };
                    }
                    return {};
                },
            },
        };
    }

    // --- LIST ITEM (Chung cho cả hai) ---
    components.MuiListItem = {
        styleOverrides: {
            root: ({ theme, ownerState }) => ({
                color: theme.palette.text.primary,
            }),
        },
    }

    // ========================================================================
    // 5. MERGE CẤU HÌNH TỪ CONFIG.THEME (GIỮ NGUYÊN)
    // ========================================================================
    if (config.theme) {
        const mergeComponentStyleOverrides = (componentKey, stylesToMergeFromConfig) => {
            if (!components[componentKey]) components[componentKey] = { styleOverrides: {} };
            else if (!components[componentKey].styleOverrides) components[componentKey].styleOverrides = {};

            for (const slotKey in stylesToMergeFromConfig) {
                const existingSlotStyle = components[componentKey].styleOverrides[slotKey];
                const newSlotStyle = stylesToMergeFromConfig[slotKey];

                if (typeof existingSlotStyle === 'function' || typeof newSlotStyle === 'function') {
                    components[componentKey].styleOverrides[slotKey] = newSlotStyle;
                } else if (typeof newSlotStyle === 'object' && newSlotStyle !== null) {
                    components[componentKey].styleOverrides[slotKey] = { ...existingSlotStyle, ...newSlotStyle };
                } else {
                    components[componentKey].styleOverrides[slotKey] = newSlotStyle;
                }
            }
        };

        for (const componentKey in config.theme) {
            if (componentKey !== "for_agent" && typeof config.theme[componentKey] === 'object') {
                mergeComponentStyleOverrides(componentKey, config.theme[componentKey]);
            }
        }

        if (isMobile && config.theme.for_agent && typeof config.theme.for_agent === 'object') {
            for (const componentKey in config.theme.for_agent) {
                if (typeof config.theme.for_agent[componentKey] === 'object') {
                    mergeComponentStyleOverrides(componentKey, config.theme.for_agent[componentKey]);
                }
            }
        }
    }
    
    return createTheme({
        palette,
        typography, // Thêm typography vào đây
        components,
    });
}

export default getTheme;